import React, { lazy, Suspense } from "react";
import Header from "./components/header/Header";
import "./App.css";
import { Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import NotFound from "./pages/NotFound";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "styled-components";
import { lightTheme, darkTheme } from "./theme/theme";
import { useDarkMode } from "./helper/Generic/useDarkMode";
import { useSelector } from "react-redux";
import { selectUser } from "./Redux/user/user";
import PermissionsGate from "./helper/Generic/PermissionGate";
import { SCOPES } from "./helper/Generic/Permissions";
import { isMobile } from "react-device-detect";
import MobileHeader from "./components/Mobile/MobileHeader/MobileHeader";
import { Box, CircularProgress } from "@mui/material";

// Lazy-loaded route components
const Login = lazy(() => import("./pages/authentication/Login"));
const Register = lazy(() => import("./pages/authentication/Register"));
const NewPassword = lazy(() => import("./pages/authentication/NewPassword"));
const ResetPassword = lazy(() => import("./pages/authentication/ResetPassword"));
const Pricing = lazy(() => import("./pages/Pricing/Pricing"));
const UserProfile = lazy(() => import("./pages/user/UserProfile"));
const Betting = lazy(() => import("./pages/Betting/Betting"));
const BetsListPage = lazy(() => import("./pages/Betting/BetsListPage"));
const LoggedBets = lazy(() => import("./pages/Betting/LoggedBets"));
const RetailLoggedBets = lazy(() => import("./pages/Betting/RetailLoggedBets"));
const Leagues = lazy(() => import("./pages/Leagues/Leagues"));
const Archived = lazy(() => import("./pages/Leagues/Archived"));
const ActiveSelections = lazy(() => import("./pages/Leagues/ActiveSelections"));
const SquadManager = lazy(() => import("./pages/Leagues/SquadManager"));
const PlayersData = lazy(() => import("./pages/Leagues/PlayersData"));
const MatchPreview = lazy(() => import("./pages/Leagues/MatchPreview"));
const Audit = lazy(() => import("./pages/Audit/Audit"));
const ProfileMobile = lazy(() => import("./pages/Mobile/ProfileMobile/ProfileMobile"));
const MatchPreviewMobile = lazy(() => import("./pages/Mobile/MatchPreview/MatchPreviewMobile"));



function App() {
  const UNAUTHORIZED_PAGE = "unauthorized-page";

  const [theme, toggleTheme] = useDarkMode();
  const themeMode = theme === "light" ? lightTheme : darkTheme;

  const user = useSelector(selectUser);

  const location = window.location;


  return (
    <>
      <ThemeProvider theme={themeMode}>
        <ToastContainer autoClose={2000} />
        {user && (isMobile ? <MobileHeader /> : <Header theme={theme} toggleTheme={toggleTheme} />)}
        <ChunkErrorBoundary>
        <Suspense fallback={null}>
        <Routes>
          <Route path="*" element={<NotFound />} />
          <Route path="/login" element={<Login />} />
          <Route path="/users/accept_invite/" element={<Register />} />
          <Route path="/forgotPassword" element={<ResetPassword />} />
          <Route path="/users/reset_password/" element={<NewPassword />} />

          <Route element={<ProtectedRoute user={user} />}>
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/pricing/:bookmaker_id_param"
              element={<Pricing />}
            />

            <Route path="/userprofile" element={isMobile ? <ProfileMobile /> : <UserProfile />} />
            {/* Nested Routes Start */}

            <Route
              path="/betting"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canBet]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  <Betting />
                </PermissionsGate>
              }
            >
              <Route
                path="/betting/bets_list"
                element={
                  <PermissionsGate
                    scopes={[SCOPES.canBet]}
                    errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                  >
                    <BetsListPage />
                  </PermissionsGate>
                }
              ></Route>

              <Route
                path="/betting/asia_logged_bets"
                element={
                  <PermissionsGate
                    scopes={[SCOPES.canBet]}
                    errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                  >
                    <LoggedBets />
                  </PermissionsGate>
                }
              ></Route>

              <Route
                path="/betting/retail_logged_bets"
                element={
                  <PermissionsGate
                    scopes={[SCOPES.canBet]}
                    errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                  >
                    <RetailLoggedBets />
                  </PermissionsGate>
                }
              ></Route>
            </Route>

            {/* Nested Routes End */}

            <Route
              path="/leagues"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAnalyze]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >

                  <Leagues />
                </PermissionsGate>
              }
            />

            <Route
              path="/leagues/archived"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAnalyze]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  <Archived />
                </PermissionsGate>
              }
            />

            <Route
              path="/admin"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAdmin]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  <ActiveSelections />
                </PermissionsGate>
              }
            />

            <Route
              path="/leagues/squad_manager"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAnalyze]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  <SquadManager />
                </PermissionsGate>
              }
            />
            <Route
              path="/leagues/players_data"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAnalyze]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  <PlayersData />
                </PermissionsGate>
              }
            />
            <Route
              path="/leagues/match_preview"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAnalyze]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  {isMobile ? <MatchPreviewMobile /> : <MatchPreview />}

                </PermissionsGate>
              }
            />

            <Route
              path="/audit"
              element={
                <PermissionsGate
                  scopes={[SCOPES.canAudit]}
                  errorProps={{ pageAuthorization: UNAUTHORIZED_PAGE }}
                >
                  <Audit />
                </PermissionsGate>
              }
            />
          </Route>
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
      </ThemeProvider>
    </>
  );
}

export default App;
